function parseBool(values: string[]): boolean {
  if (values && values.length > 0) {
    return values[0] === 'true'
  }
  return false
}

export function isExternalFeature(id: string) {
  return id.startsWith('jpsxt-')
}

export function fromQueryString(query: string): SearchQuery {
  const q: SearchQuery = {}
  const map = getQueryMap(query)

  if (map['csid']) q.csid = map['csid'][0]

  if (map['from']) q.from = Number(map['from'])
  if (!Number.isInteger(q.from) || q.from < 0) q.from = 0

  if (map['size']) q.size = Number(map['size'])
  if (!Number.isInteger(q.size) || q.size < 0) q.size = 20

  if (map['sort']) {
    q.sort = map['sort']
  }

  if (map['keyword']) {
    q.keyword = map['keyword']
  }
  if (map['subquery']) {
    q.subquery = map['subquery'].map(sq => JSON.parse(sq))
  }

  q.exists = map['exists']
  q.image = map['image']
  if (map['text2image']) q.text2image = map['text2image'][0]

  q.query = {}
  q.filter = {}
  q.range = {}
  q.geoDistance = {}
  Object.keys(map).forEach(key => {
    if (key.startsWith('q-')) {
      q.query[key.substring(2)] = map[key]
    }
    if (key.startsWith('f-')) {
      q.filter[key.substring(2)] = map[key]
    }
    if (key.startsWith('r-')) {
      q.range[key.substring(2)] = map[key].map(v => {
        const spl = v.split(/,/, 2)
        if (spl.length >= 2) {
          return { from: spl[0], to: spl[1] }
        }
      })
    }
    if (key.startsWith('g-')) {
      q.geoDistance[key.substring(2)] = map[key].map(v => {
        const spl = v.split(/,/, 3)
        if (spl.length >= 3) {
          return { lat: Number(spl[0]), lon: Number(spl[1]), distance: spl[2] }
        }
      })
    }
    if (key.startsWith('fc-')) {
      //互換性のため
      q.filter[key.substring(3)] = map[key]
    }
  })
  return q
}

export function fromQuery(map: { [key: string]: string[] }): SearchQuery {
  const q: SearchQuery = {}

  if (map['csid']) q.csid = map['csid'][0]

  if (map['from']) q.from = Number(map['from'])
  if (!Number.isInteger(q.from) || q.from < 0) q.from = 0

  if (map['size']) q.size = Number(map['size'])
  if (!Number.isInteger(q.size) || q.size < 0) q.size = 20

  if (map['sort']) {
    q.sort = map['sort']
  }

  if (map['keyword']) {
    q.keyword = map['keyword']
  }
  if (map['subquery']) {
    q.subquery = map['subquery'].map(sq => JSON.parse(sq))
  }

  q.exists = map['exists']
  q.image = map['image']
  if (map['text2image']) q.text2image = map['text2image'][0]

  q.query = {}
  q.filter = {}
  q.range = {}
  q.geoDistance = {}
  Object.keys(map).forEach(key => {
    if (key.startsWith('q-')) {
      q.query[key.substring(2)] = map[key]
    }
    if (key.startsWith('f-')) {
      q.filter[key.substring(2)] = map[key]
    }
    if (key.startsWith('r-')) {
      q.range[key.substring(2)] = map[key].map(v => {
        const spl = v.split(/,/, 2)
        if (spl.length >= 2) {
          return { from: spl[0], to: spl[1] }
        }
      })
    }
    if (key.startsWith('g-')) {
      q.geoDistance[key.substring(2)] = map[key].map(v => {
        const spl = v.split(/,/, 3)
        if (spl.length >= 3) {
          return { lat: Number(spl[0]), lon: Number(spl[1]), distance: spl[2] }
        }
      })
    }
    if (key.startsWith('fc-')) {
      //互換性のため
      q.filter[key.substring(3)] = map[key]
    }
  })
  return q
}

function filter(input: string[]): string[] {
  if (input && Array.isArray(input))
    return input?.filter(s => s != null && s !== '')
  return []
}

export function toSeqrchQueryUrlMap(sq: SearchQuery) {
  const q: { [key: string]: any[] } = {}
  if (sq.csid) q['csid'] = [sq.csid]
  if (sq.permission) q['jps-act'] = [sq.permission]
  if (sq.from != null) q['from'] = [sq.from]
  if (sq.size != null && sq.size !== 20) q['size'] = [sq.size]
  if (sq.sort) q['sort'] = filter(sq.sort)
  if (sq.keyword) q['keyword'] = filter(sq.keyword)
  if (sq.exists) q['exists'] = filter(sq.exists)
  if (sq.image) q['image'] = filter(sq.image)
  if (sq.text2image) q['text2image'] = filter([sq.text2image])
  if (sq.facet) q['facet'] = filter(sq.facet)
  if (sq.subquery) {
    q['subquery'] = sq.subquery.map(s => JSON.stringify(s))
  }
  if (sq.query) {
    Object.keys(sq.query).forEach(key => {
      const val: string[] = sq.query[key]
      if (val) q['q-' + key] = filter(val)
    })
  }
  if (sq.range) {
    Object.keys(sq.range).forEach(key => {
      const val = sq.range[key]
      if (val)
        q['r-' + key] = val.map(
          s =>
            (s.from || '') +
            ',' +
            (s.to || ''),
        )
    })
  }
  if (sq.geoDistance) {
    Object.keys(sq.geoDistance).forEach(key => {
      const val = sq.geoDistance[key]
      if (val)
        q['g-' + key] = val.map(
          s => s.lat + ',' + s.lon + ',' + s.distance,
        )
    })
  }
  if (sq.filter) {
    Object.keys(sq.filter).forEach(key => {
      const val: string[] = sq.filter[key]
      if (val) q['f-' + key] = filter(val)
    })
  }

  return q
}

export function isEqualQueryExpression(value: string, words: string[] = []) {
  const splited = split(value || '')
  return equals(splited, words)
}

export function toSearchQueryString(q: SearchQuery) {
  return toQueryString(toSeqrchQueryUrlMap(q))
}

export function searchTo(query: SearchQuery) {
  return `/csearch/${query.csid}${toSearchQueryString(query)}`
}

export const systemCrossSearchId = (): string => {
  const config = useRuntimeConfig()
  return config.public.crossSearchId as string
}

export const systemImageSearchId = (): string => {
  const config = useRuntimeConfig()
  return config.public.imageSearchId as string
}

export const systemGallerySearchId = (): string => {
  const config = useRuntimeConfig()
  return config.public.gallerySearchId as string
}

export const systemCrossSearchCs = (): CustomSearchDefinition => {
  const config = useRuntimeConfig()
  if (config.public.isHina) {
    return HINA_CROSS
  } else {
    return JPS_CROSS
  }
}

export const systemGallerySearchCs = (): CustomSearchDefinition => {
  const config = useRuntimeConfig()
  if (config.public.isHina) {
    return HINA_GALLERYCS
  } else {
    return JPS_GALLERYCS
  }
}

